import { useEffect } from "react";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { Avatar } from "@/components/atoms/Avatar";
import { Pill } from "@/components/atoms/Pill";

interface Props {
  totalVolume24h?: number;
  openMarkets?: number;
}

export function Navbar({ totalVolume24h = 48_200_000, openMarkets = 0 }: Props) {
  const userId = useStore((s) => s.userId);
  const userEmail = useStore((s) => s.userEmail);
  const setSearchOpen = useStore((s) => s.setSearchOpen);
  const setWalletOpen = useStore((s) => s.setWalletOpen);
  const setNotifOpen = useStore((s) => s.setNotifOpen);
  const setMobileNavOpen = useStore((s) => s.setMobileNavOpen);

  // Cmd+K shortcut.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setSearchOpen]);

  return (
    <div className="glass sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line px-3 sm:gap-3 sm:px-4">
      {/* Sotto `lg` la sidebar e' un drawer: serve un modo per aprirlo. */}
      <button
        type="button"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-line text-text-2 hover:border-line-strong lg:hidden"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open navigation"
      >
        <Icon.Menu size={16} />
      </button>

      <button
        type="button"
        className="flex min-w-0 items-center gap-2 rounded-[10px] border border-line bg-bg-2 px-3 py-[7px] text-[12px] text-muted hover:border-line-strong hover:text-text-2"
        onClick={() => setSearchOpen(true)}
        aria-label="Search markets"
      >
        <Icon.Search size={14} />
        <span className="hidden sm:inline">Search markets…</span>
        <span className="ml-3 hidden rounded border border-line-strong bg-bg-3 px-[6px] py-[1px] text-[10px] tracking-wider text-muted sm:inline">
          ⌘K
        </span>
      </button>

      <div className="flex-1" />

      <div className="navbar-stats hidden items-center gap-2 lg:flex">
        <Pill>
          <span className="text-muted">24h</span>
          <span className="font-mono text-text">${(totalVolume24h / 1e6).toFixed(1)}M</span>
        </Pill>
        <Pill variant="accent">
          <span className="text-text-2/80">Markets</span>
          <span className="font-mono">{openMarkets}</span>
        </Pill>
      </div>

      <button
        className="relative grid h-9 w-9 place-items-center rounded-full border border-line text-text-2 hover:border-line-strong"
        onClick={() => setNotifOpen(true)}
        aria-label="Notifications"
      >
        <Icon.Bell size={16} />
        <span className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-no" />
      </button>

      {userId ? (
        <div className="flex items-center gap-2">
          <Avatar name={userEmail ?? "?"} size={32} />
        </div>
      ) : (
        <button
          className="btn btn-primary"
          onClick={() => setWalletOpen(true)}
        >
          <Icon.Wallet size={14} />
          <span className="wallet-label hidden md:inline">Connect</span>
        </button>
      )}
    </div>
  );
}
